<template>
  <div class="schedules-container">
    <!-- 页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <h1 class="page-title">
          <i class="el-icon-time"></i>
          定时任务管理
        </h1>
        <p class="page-description">管理系统定时任务，包括任务信息查看、启用/禁用、删除等操作</p>
      </div>
    </div>

    <!-- 搜索和操作区域 -->
    <div class="search-section">
      <el-card class="search-card" shadow="never">
        <div class="search-form">
          <div class="search-row">
            <div class="search-item">
              <el-input
                v-model="searchForm.keyword"
                placeholder="搜索任务名称"
                prefix-icon="el-icon-search"
                clearable
                @keyup.enter.native="handleSearch"
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.username"
                placeholder="搜索用户名"
                prefix-icon="el-icon-user"
                clearable
                @keyup.enter.native="handleSearch"
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.user_id"
                placeholder="用户ID"
                clearable
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.robot_id"
                placeholder="机器人ID"
                clearable
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-input
                v-model="searchForm.task_id"
                placeholder="任务ID"
                clearable
                class="search-input"
              />
            </div>
            <div class="search-item">
              <el-select
                v-model="searchForm.type"
                placeholder="任务类型"
                clearable
                class="status-select"
              >
                <el-option label="全部类型" value="" />
                <el-option label="每天执行" value="daily" />
                <el-option label="每周执行" value="weekly" />
                <el-option label="法定工作日" value="workday" />
                <el-option label="法定节假日" value="holiday" />
                <el-option label="间隔执行" value="interval" />
              </el-select>
            </div>
            <div class="search-item">
              <el-select
                v-model="searchForm.action"
                placeholder="执行动作"
                clearable
                class="status-select"
              >
                <el-option label="全部动作" value="" />
                <el-option label="关" value="off" />
                <el-option label="开" value="single_on" />
                <el-option label="左开" value="double_left" />
                <el-option label="右开" value="double_right" />
              </el-select>
            </div>
            <div class="search-item">
              <el-select
                v-model="searchForm.enabled"
                placeholder="启用状态"
                clearable
                class="status-select"
              >
                <el-option label="全部状态" value="" />
                <el-option label="已启用" value="true" />
                <el-option label="已禁用" value="false" />
              </el-select>
            </div>
            <div class="search-actions">
              <el-button type="primary" icon="el-icon-search" @click="handleSearch">
                搜索
              </el-button>
              <el-button icon="el-icon-refresh" @click="handleReset">
                重置
              </el-button>
            </div>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 操作按钮区域 -->
    <div class="action-section">
      <el-card class="action-card" shadow="never">
        <div class="action-buttons">
          <el-button
            type="danger"
            icon="el-icon-delete"
            :disabled="selectedTasks.length === 0"
            @click="handleBatchDelete"
            class="batch-delete-btn"
          >
            批量删除 ({{ selectedTasks.length }})
          </el-button>
        </div>
      </el-card>
    </div>

    <!-- 数据表格 -->
    <div class="table-section">
      <el-card class="table-card" shadow="never">
        <el-table
          :data="taskList"
          v-loading="loading"
          @selection-change="handleSelectionChange"
          stripe
          class="task-table"
          :header-cell-style="{ background: '#f5f7fa', color: '#606266', textAlign: 'center' }"
        >
          <el-table-column type="selection" width="55" align="center" />
          <el-table-column prop="id" label="任务ID" width="80" align="center" />
          <el-table-column prop="name" label="任务名称" min-width="150" align="center">
            <template slot-scope="scope">
              <div class="task-name-cell">
                <i class="el-icon-time task-icon"></i>
                <span class="task-name-text">{{ scope.row.name }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="user_id" label="用户ID" width="100" align="center" />
          <el-table-column prop="username" label="用户名" min-width="120" align="center" show-overflow-tooltip />
          <el-table-column prop="robot_id" label="机器人ID" width="100" align="center" />
          <el-table-column prop="robot_name" label="机器人名称" min-width="150" align="center" show-overflow-tooltip />
          <el-table-column prop="type" label="任务类型" width="110" align="center">
            <template slot-scope="scope">
              <el-tag :type="getTypeColor(scope.row.type)" size="small">
                {{ formatType(scope.row.type) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column prop="action" label="执行动作" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="getActionColor(scope.row.action)" size="small">
                {{ formatAction(scope.row.action) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="执行时间" min-width="200" align="center">
            <template slot-scope="scope">
              <div class="schedule-time">
                {{ formatScheduleTime(scope.row) }}
              </div>
            </template>
          </el-table-column>
          <el-table-column prop="enabled" label="状态" width="100" align="center">
            <template slot-scope="scope">
              <el-tag :type="scope.row.enabled ? 'success' : 'info'" size="small">
                {{ scope.row.enabled ? '已启用' : '已禁用' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="操作" width="280" fixed="right" align="center">
            <template slot-scope="scope">
              <div class="action-buttons">
                <el-button
                  type="text"
                  icon="el-icon-view"
                  @click="handleView(scope.row)"
                  class="action-btn view-btn"
                >
                  详情
                </el-button>
                <el-button
                  type="text"
                  :icon="scope.row.enabled ? 'el-icon-video-pause' : 'el-icon-video-play'"
                  @click="handleToggle(scope.row)"
                  class="action-btn toggle-btn"
                >
                  {{ scope.row.enabled ? '禁用' : '启用' }}
                </el-button>
                <el-button
                  type="text"
                  icon="el-icon-delete"
                  @click="handleDelete(scope.row)"
                  class="action-btn delete-btn"
                >
                  删除
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-wrapper">
          <el-pagination
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            :current-page="pagination.page"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="pagination.pageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            class="pagination"
          />
        </div>
      </el-card>
    </div>

    <!-- 任务详情对话框 -->
    <el-dialog
      title="定时任务详情"
      :visible.sync="detailDialogVisible"
      width="700px"
      class="detail-dialog"
    >
      <div v-if="currentTask" class="task-detail">
        <div class="detail-section">
          <h3>基本信息</h3>
          <div class="detail-grid">
            <div class="detail-item">
              <label>任务ID：</label>
              <span>{{ currentTask.id }}</span>
            </div>
            <div class="detail-item">
              <label>任务名称：</label>
              <span>{{ currentTask.name }}</span>
            </div>
            <div class="detail-item">
              <label>用户ID：</label>
              <span>{{ currentTask.user_id }}</span>
            </div>
            <div class="detail-item">
              <label>用户名：</label>
              <span>{{ currentTask.username }}</span>
            </div>
            <div class="detail-item">
              <label>机器人ID：</label>
              <span>{{ currentTask.robot_id }}</span>
            </div>
            <div class="detail-item">
              <label>机器人名称：</label>
              <span>{{ currentTask.robot_name }}</span>
            </div>
            <div class="detail-item">
              <label>任务类型：</label>
              <el-tag :type="getTypeColor(currentTask.type)" size="small">
                {{ formatType(currentTask.type) }}
              </el-tag>
            </div>
            <div class="detail-item">
              <label>执行动作：</label>
              <el-tag :type="getActionColor(currentTask.action)" size="small">
                {{ formatAction(currentTask.action) }}
              </el-tag>
            </div>
            <div class="detail-item">
              <label>启用状态：</label>
              <el-tag :type="currentTask.enabled ? 'success' : 'info'" size="small">
                {{ currentTask.enabled ? '已启用' : '已禁用' }}
              </el-tag>
            </div>
            <div class="detail-item">
              <label>执行时间：</label>
              <span>{{ formatScheduleTime(currentTask) }}</span>
            </div>
            <div class="detail-item">
              <label>上次执行：</label>
              <span>{{ currentTask.last_executed_at || '-' }}</span>
            </div>
            <div class="detail-item">
              <label>下次执行：</label>
              <span>{{ currentTask.next_execute_at || '-' }}</span>
            </div>
            <div class="detail-item">
              <label>创建时间：</label>
              <span>{{ currentTask.created_at }}</span>
            </div>
            <div class="detail-item">
              <label>更新时间：</label>
              <span>{{ currentTask.updated_at }}</span>
            </div>
          </div>
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  getAdminScheduleList,
  getAdminScheduleDetail,
  toggleAdminSchedule,
  deleteAdminSchedule,
  batchDeleteAdminSchedules
} from '@/api/schedule'

export default {
  name: 'AdminSchedules',
  data() {
    return {
      loading: false,
      taskList: [],
      selectedTasks: [],
      searchForm: {
        keyword: '',
        username: '',
        user_id: '',
        robot_id: '',
        task_id: '',
        type: '',
        action: '',
        enabled: ''
      },
      pagination: {
        page: 1,
        pageSize: 10,
        total: 0
      },
      detailDialogVisible: false,
      currentTask: null
    }
  },
  created() {
    this.getTaskList()
  },
  methods: {
    // 获取任务列表
    async getTaskList() {
      this.loading = true
      try {
        const params = {
          page: this.pagination.page,
          pageSize: this.pagination.pageSize,
          keyword: this.searchForm.keyword,
          username: this.searchForm.username,
          user_id: this.searchForm.user_id,
          robot_id: this.searchForm.robot_id,
          task_id: this.searchForm.task_id,
          type: this.searchForm.type,
          action: this.searchForm.action,
          enabled: this.searchForm.enabled
        }
        const response = await getAdminScheduleList(params)
        this.taskList = response.data.list || []
        this.pagination.total = response.data.total
      } finally {
        this.loading = false
      }
    },

    // 搜索
    handleSearch() {
      this.pagination.page = 1
      this.getTaskList()
    },

    // 重置搜索
    handleReset() {
      this.searchForm = {
        keyword: '',
        username: '',
        user_id: '',
        robot_id: '',
        task_id: '',
        type: '',
        action: '',
        enabled: ''
      }
      this.pagination.page = 1
      this.getTaskList()
    },

    // 分页大小改变
    handleSizeChange(size) {
      this.pagination.pageSize = size
      this.pagination.page = 1
      this.getTaskList()
    },

    // 当前页改变
    handleCurrentChange(page) {
      this.pagination.page = page
      this.getTaskList()
    },

    // 选择改变
    handleSelectionChange(selection) {
      this.selectedTasks = selection
    },

    // 查看任务详情
    async handleView(task) {
      try {
        const response = await getAdminScheduleDetail(task.id)
        this.currentTask = response.data
        this.detailDialogVisible = true
      } catch (error) {
        this.$message.error('获取任务详情失败')
      }
    },

    // 切换任务启用状态
    async handleToggle(task) {
      try {
        await toggleAdminSchedule(task.id, !task.enabled)
        this.$message.success(task.enabled ? '已禁用任务' : '已启用任务')
        this.getTaskList()
      } catch (error) {
        // 错误已由全局处理
      }
    },

    // 删除任务
    handleDelete(task) {
      this.$confirm(`确定要删除任务 "${task.name}" 吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await deleteAdminSchedule(task.id)
        this.$message.success('删除成功')
        this.getTaskList()
      }).catch(() => {})
    },

    // 批量删除
    handleBatchDelete() {
      if (this.selectedTasks.length === 0) {
        this.$message.warning('请选择要删除的任务')
        return
      }

      this.$confirm(`确定要删除选中的 ${this.selectedTasks.length} 个任务吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        const ids = this.selectedTasks.map(task => task.id)
        await batchDeleteAdminSchedules(ids)
        this.$message.success('批量删除成功')
        this.getTaskList()
      }).catch(() => {})
    },

    // 格式化任务类型
    formatType(type) {
      const typeMap = {
        'daily': '每天执行',
        'weekly': '每周执行',
        'workday': '法定工作日',
        'holiday': '法定节假日',
        'interval': '间隔执行'
      }
      return typeMap[type] || type
    },

    // 获取任务类型颜色
    getTypeColor(type) {
      const colorMap = {
        'daily': 'success',
        'weekly': 'warning',
        'workday': 'success',
        'holiday': 'danger',
        'interval': 'primary'
      }
      return colorMap[type] || ''
    },

    // 格式化执行动作
    formatAction(action) {
      const actionMap = {
        'off': '关',
        'single_on': '开',
        'double_left': '左开',
        'double_right': '右开'
      }
      return actionMap[action] || action
    },

    // 获取动作颜色
    getActionColor(action) {
      const colorMap = {
        'off': 'info',
        'single_on': 'success',
        'double_left': 'warning',
        'double_right': 'danger'
      }
      return colorMap[action] || ''
    },

    // 格式化定时任务执行时间
    formatScheduleTime(task) {
      // 如果是间隔执行类型
      if (task.type === 'interval' && task.interval_seconds && task.interval_seconds > 0) {
        const seconds = task.interval_seconds
        if (seconds < 60) {
          return `每 ${seconds} 秒执行一次`
        } else if (seconds < 3600) {
          const minutes = Math.floor(seconds / 60)
          const remainSeconds = seconds % 60
          return remainSeconds > 0
            ? `每 ${minutes} 分 ${remainSeconds} 秒执行一次`
            : `每 ${minutes} 分钟执行一次`
        } else {
          const hours = Math.floor(seconds / 3600)
          const minutes = Math.floor((seconds % 3600) / 60)
          return minutes > 0
            ? `每 ${hours} 小时 ${minutes} 分钟执行一次`
            : `每 ${hours} 小时执行一次`
        }
      }

      // 格式化时分秒
      const hour = task.hour !== null && task.hour !== undefined ? task.hour : 0
      const minute = task.minute !== null && task.minute !== undefined ? task.minute : 0
      const second = task.second !== null && task.second !== undefined ? task.second : 0
      const timeStr = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`

      // 如果是每周类型且有weekdays
      if (task.type === 'weekly' && task.weekdays) {
        const weekdaysArr = task.weekdays.split(',').map(d => d.trim())
        const weekdayNames = {
          '1': '周一', '2': '周二', '3': '周三', '4': '周四',
          '5': '周五', '6': '周六', '7': '周日'
        }
        const weekdayStr = weekdaysArr.map(d => weekdayNames[d] || d).join('、')
        return `${weekdayStr} ${timeStr}`
      }

      if (task.type === 'workday') {
        return `法定工作日 ${timeStr}`
      }
      if (task.type === 'holiday') {
        return `法定节假日 ${timeStr}`
      }
      if (task.type === 'daily') {
        return `每天 ${timeStr}`
      }

      return timeStr
    }
  }
}
</script>

<style scoped>
.schedules-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: 100vh;
}

/* 页面头部 */
.page-header {
  margin-bottom: 20px;
}

.header-content {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  padding: 30px;
  border-radius: 12px;
  color: white;
  box-shadow: 0 4px 20px rgba(102, 126, 234, 0.3);
}

.page-title {
  margin: 0 0 10px 0;
  font-size: 28px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 12px;
}

.page-description {
  margin: 0;
  opacity: 0.9;
  font-size: 16px;
}

/* 搜索区域 */
.search-section {
  margin-bottom: 20px;
}

.search-card {
  border-radius: 12px;
  border: none;
}

.search-form {
  padding: 10px 0;
}

.search-row {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.search-item {
  flex: 1;
  min-width: 200px;
}

.search-input {
  width: 100%;
}

.status-select {
  width: 100%;
}

.search-actions {
  display: flex;
  gap: 12px;
}

/* 操作区域 */
.action-section {
  margin-bottom: 20px;
}

.action-card {
  border-radius: 12px;
  border: none;
}

.batch-delete-btn {
  border-radius: 8px;
  padding: 12px 24px;
  font-weight: 500;
}

/* 表格区域 */
.table-section {
  margin-bottom: 20px;
}

.table-card {
  border-radius: 12px;
  border: none;
  overflow: hidden;
}

.task-table {
  width: 100%;
}

.task-name-cell {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.task-icon {
  color: #667eea;
  font-size: 18px;
}

.task-name-text {
  font-weight: 500;
  color: #303133;
}

.schedule-time {
  color: #606266;
  font-size: 14px;
  line-height: 1.5;
}

.action-buttons {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: center;
}

.action-btn {
  padding: 4px 4px;
  border-radius: 4px;
  font-size: 14px;
  transition: all 0.3s;
}

.view-btn {
  color: #409eff;
}

.view-btn:hover {
  background-color: #ecf5ff;
  color: #337ecc;
}

.toggle-btn {
  color: #e6a23c;
}

.toggle-btn:hover {
  background-color: #fdf6ec;
  color: #b88230;
}

.delete-btn {
  color: #f56c6c;
}

.delete-btn:hover {
  background-color: #fef0f0;
  color: #c45656;
}

/* 分页 */
.pagination-wrapper {
  display: flex;
  justify-content: center;
  margin-top: 20px;
  padding: 20px 0;
}

.pagination {
  background: white;
  padding: 16px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* 对话框 */
.detail-dialog .el-dialog__body {
  padding: 20px;
}

.task-detail {
  padding: 20px 0;
}

.detail-section h3 {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
  border-bottom: 2px solid #e4e7ed;
  padding-bottom: 10px;
}

.detail-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.detail-item {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;
  border-left: 4px solid #667eea;
}

.detail-item label {
  font-weight: 600;
  color: #606266;
  margin-right: 8px;
  min-width: 100px;
}

.detail-item span {
  color: #303133;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .schedules-container {
    padding: 10px;
  }

  .page-title {
    font-size: 24px;
  }

  .search-row {
    flex-direction: column;
    align-items: stretch;
  }

  .search-item {
    min-width: auto;
  }

  .search-actions {
    justify-content: center;
  }

  .action-buttons {
    justify-content: center;
  }

  .detail-grid {
    grid-template-columns: 1fr;
  }
}
</style>
